import { X } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { usePersistedState } from '../lib/usePersistedState';

/**
 * Inline caption banner for the top of admin pages whose name alone
 * doesn't telegraph what they do (Presenter brief, News desk, Stories,
 * Events). Sits directly under the PageHeader and explains, in one
 * sentence, what the page is and how an editor uses it.
 *
 * Visual: subtle tinted strip with an optional icon + a dismiss button
 * on the right. Once dismissed it stays dismissed across sessions on
 * this device (per-`id` localStorage flag) — the helper text is meant
 * for orientation, not nagging.
 *
 * `id` is required. It's the localStorage key suffix that scopes the
 * dismissal state, so each page's caption is dismissed independently.
 */
interface PageCaptionProps {
  /** Stable identifier for the dismissal state — e.g. 'presenter-brief',
   *  'news-desk'. Used as a localStorage key suffix. */
  id: string;
  icon?: ComponentType<{ className?: string }>;
  children: ReactNode;
  className?: string;
}

const STORAGE_PREFIX = 'page-caption-dismissed:';

export function PageCaption({
  id,
  icon: Icon,
  children,
  className,
}: PageCaptionProps): JSX.Element | null {
  const [dismissed, setDismissed] = usePersistedState<boolean>(
    `${STORAGE_PREFIX}${id}`,
    false,
  );
  if (dismissed) return null;

  return (
    <div
      className={[
        'mx-5 mb-4 flex items-start gap-2.5 rounded-md border border-line bg-surface-tinted/60 px-3.5 py-2.5',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> : null}
      <p className="flex-1 text-[12.5px] leading-snug text-ink-muted">{children}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="-mr-1 -mt-0.5 shrink-0 rounded p-1 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink"
        aria-label="Dismiss caption"
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
